/** @jsxImportSource @emotion/react */
import { type ReactNode, useContext } from 'react';

import { Button } from 'components/Button';
import { Link } from 'components/Link';
import { Modal } from 'components/Modal';
import { Text } from 'components/Text';
import { StylingContext } from 'context/Styling';
import { useStyles } from 'utils/useStyles';

import { termsAndConditionsModalStyles } from './TermsAndConditionsModal.styles';

interface TermLink {
  id: string;
  label: string;
  to: string;
}

const TERMS_ORDER = ['terms', 'msa', 'privacy-policy'];

const TERMS_CONFIG: Record<string, TermLink> = {
  terms: { id: 'terms-modal-tos', label: 'Terms of Service', to: '/legal/terms' },
  msa: { id: 'terms-modal-msa', label: 'Master Services Agreement', to: '/legal/msa' },
  'privacy-policy': { id: 'terms-modal-privacy', label: 'Privacy Policy', to: '/legal/privacy-policy' },
};

export const getPendingTermsLinks = (pendingTerms: string[]): TermLink[] =>
  TERMS_ORDER.filter((key) => pendingTerms.includes(key)).map((key) => TERMS_CONFIG[key]);

export const joinLinks = (links: TermLink[]): ReactNode[] =>
  links.flatMap((link, i) => {
    const el = <Link key={link.id} id={link.id} label={link.label} to={link.to} type="external" />;
    if (i === 0) return [el];
    if (links.length === 2) return [' and ', el];
    if (i < links.length - 1) return [', ', el];
    return [', and ', el];
  });

export interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onLogout: () => void;
  /** When true, disables Accept and shows loading (e.g. while acceptance request runs). Log out stays enabled. */
  acceptDisabled?: boolean;
  pendingTerms: string[];
}

export const TermsAndConditionsModal = ({
  isOpen,
  onAccept,
  onLogout,
  acceptDisabled = false,
  pendingTerms,
}: TermsAndConditionsModalProps) => {
  const {
    viewport: { md },
  } = useContext(StylingContext);
  const styles = useStyles(termsAndConditionsModalStyles);
  const links = getPendingTermsLinks(pendingTerms);

  return (
    <Modal isOpen={isOpen} ariaLabel="Accept updated terms and conditions">
      <div css={styles.content}>
        <div css={styles.header}>
          <Text component="h2" css={styles.heading} type="large">
            Updated Terms
          </Text>
        </div>

        <Text css={styles.description} type="normal">
          {links.length > 0 ? <>We&apos;ve revised our {joinLinks(links)}.</> : <>We&apos;ve revised our terms.</>} By
          accepting you agree to our updated terms and policy. Please review these important documents.{' '}
          <Link id="terms-modal-learn-more" label="Learn more" to="/legal/updates/" type="external" />
        </Text>

        <div css={styles.actions}>
          <Button
            id="terms-modal-logout"
            label="Log out"
            onClick={onLogout}
            variant="white"
            width={md ? 'auto' : 'full'}
            css={styles.secondaryButton}
            trackingLabel="terms-modal-logout"
          />
          <Button
            id="terms-modal-accept"
            label="Accept"
            disabled={acceptDisabled}
            isLoading={acceptDisabled}
            onClick={onAccept}
            variant="primary"
            width={md ? 'auto' : 'full'}
            css={styles.primaryButton}
            trackingLabel="terms-modal-accept"
          />
        </div>
      </div>
    </Modal>
  );
};
